<template>
  <BLayoutView
    :back-route="backRoute"
    :edit-route="editRoute"
    :title="pageTitle"
    :loading="isLoading"
    :skeleton="isLoadingAsyncData"

    :hidden-footer="isLoadingAsyncData || viewOnly"

    :save-menu-list="saveMenuList"
    :saveable="saveable"
    :editable="editable"
    :deletable="deletable"

    @delete="onDelete(single)"
    @save-menu="onSave"
  >
    <!--  -->

    <template #form>
      <ComponentForm
        ref="form"
        v-bind="viewTypeOptions"
        :data="viewTypeOptions.isCreate ? void 0 : single"
        :is-profile="isProfile"
        :skeleton="isLoadingAsyncData"
        @save="onSave()"
        @change-password="onChangePassword"
      >
        <template v-if="id" #left>
          <BLayoutExpansion title="Списки участников пользователя" class="mt-4">
            <UsersParticipantsList
              :user_id="id"
            />
          </BLayoutExpansion>

          <BLayoutExpansion title="Действия" class="mt-4">
            <UsersActionsList
              :user_id="id"
            />
          </BLayoutExpansion>
        </template>


        <template v-if="sessionList" #right>
          <UsersSessionList
            :list="sessionList"
            :loading="loadingSessions"
            @logout-session="onLogoutSession"
          />
        </template>
      </ComponentForm>
    </template>

    <template v-if="groupList">
      <BLayoutExpansion title="Группы" class="mt-4">
        <BTableCommon
          type="permission-groups"
          :list="groupList"
          :actions="[]"
        />
      </BLayoutExpansion>
    </template>

    <template v-if="publicationsList">
      <BLayoutExpansion title="Список доступных публикаций" class="mt-4">
        <BTableCommon
          type="publications-user-available"
          :list="publicationsList"
        />
      </BLayoutExpansion>
    </template>

    <!--  -->
  </BLayoutView>
</template>

<script>
import { createViewMixin } from '@/helpers/mixins/createViewMixin'
import { createDeletableMixin } from '@/helpers/mixins/createDeletableMixin'
import { UserModel as Model } from './Model'
import {
  usersStoreModule as StoreModule,
  usersMapper as StoreMapper,
} from './store'
import {
  USERS_ROUTES as ROUTES,
  createUsersModalPassword,
} from './utils'

import BLayoutView from '@/components/layouts/BLayoutView.vue'
import BLayoutExpansion from '@/components/layouts/BLayoutExpansion.vue'
import BTableCommon from '@/components/tables/BTableCommon.vue'
import ComponentForm from './components/UsersForm.vue'
import UsersSessionList from './components/UsersSessionList.vue'
import UsersActionsList from '@/views/UsersActions/UsersActionsList.vue'
import UsersParticipantsList from '@/views/UsersParticipants/UsersParticipantsList.vue'
import { authUserPermissions as Permissions } from '@/helpers/enums/permission-models'
import * as VIEW_TYPES from '@/helpers/enums/view-types'


const mixinOptions = { Model, StoreModule }
const viewMixin = createViewMixin(mixinOptions)
const deletableMixin = createDeletableMixin(mixinOptions)

/** @typedef { import('@/helpers/mixins/createViewMixin').ViewMixin<any, StoreModule> } View */
/** @typedef { import('@/helpers/mixins/createDeletableMixin').DeletableMixin<any, StoreModule> } Deletable */

export default {
  name: 'ViewUsersView',
  $_veeValidate: {
    /** @type 'new' */
    validator: 'new',
  },
  components: {
    BLayoutView,
    BLayoutExpansion,
    BTableCommon,
    ComponentForm,
    UsersSessionList,
    UsersActionsList,
    UsersParticipantsList,
  },
  mixins: [
    viewMixin,
    deletableMixin,
  ],
  data: () => ({
    backRoute: { name: ROUTES.LIST },
    isProfile: false,
    groupList: void 0,
    sessionList: void 0,
    publicationsList: void 0,
    loadingSessions: Boolean,
    saveMenuList: void 0,
  }),
  computed: {
    viewTypeOptions() {
      const { viewType } = this
      return !this.$access(Permissions.edit)
        ? {
          isCreate: false,
          isEdit: false,
          isView: true,
        }
        : {
          isCreate: viewType === VIEW_TYPES.CREATE,
          isEdit: viewType === VIEW_TYPES.EDIT,
          isView: viewType === VIEW_TYPES.VIEW,
        }
    },
    viewOnly() {
      return !this.$access(Permissions.edit) && !this.$access(Permissions.create) && !this.$access(Permissions.delete)
    },
    createRoute() {
      return {
        name: ROUTES.CREATE,
      }
    },
    editRoute() {
      const id = this.id || this.single?.id

      return {
        name: ROUTES.EDIT,
        props: { id },
      }
    },
  },
  /** @type { View & Deletable } */
  methods: {
    ...StoreMapper.mapActions([
      'getGroups',
      'getAvailablePublications',
      'getSessions',
      'logoutSession',
      'updateSecurity',
    ]),

    async asyncData() {
      if (this.viewTypeOptions.isCreate) return
      this.groupList = void 0
      this.sessionList = void 0
      this.publicationsList = void 0

      const params = { id: this.id }
      this.onGetGroups(params)
      this.onGetAvailablePublications(params)
      this.onGetSessions(params)
      await this.onGetData(params)
    },
    async onGetGroups(params) {
      if (!params.id) return

      try {
        this.groupList = await this.getGroups(params)
      } catch (e) {
        this.$notice.errors(e)
      }
    },
    async onGetAvailablePublications(params) {
      if (!params.id) return

      try {
        this.publicationsList = await this.getAvailablePublications(params)
      } catch (e) {
        this.$notice.errors(e)
      }
    },
    async onGetSessions(rawData) {
      this.loadingSessions = true

      try {
        const data = { ...rawData }
        this.sessionList = await this.getSessions(data)
      } catch (e) {
        this.$notice.errors(e)
      }

      this.loadingSessions = false
    },
    async onLogoutSession(data, toggle) {
      const model = new Model(data)
      if (toggle) toggle()

      try {
        await this.logoutSession({ payload: data })
        const message = model.response_success.logout_session
        this.$notice.create(message)
      } catch (e) {
        const message = model.response_error.logout_session
        this.$notice.errors(e, { message })
      }

      const id = this.id || this.single?.id
      const params = { id }

      const promises = [
        this.onGetSessions(params),
      ]

      try {
        await Promise.all(promises)
      } finally {
        if (toggle) toggle()
      }
    },
    async onSave(type) {
      const form = await this.$refs.form.onSubmit()
      const result = await this.onSubmit(form)
      this.$refs.form.onReset()

      const { id } = result
      const editRoute = { name: ROUTES.EDIT, params: { id } }
      this.toSave(this.backRoute, this.createRoute, editRoute, type)
    },
    toSaveGoCreate(route) {
      this.toSaveGoCommon(route)
      this.groupList = void 0
      this.sessionList = void 0
    },
    async onDelete(data) {
      const result = await this.toDelete(data)
      if (!result) return
      this.$router.replace(this.backRoute)
    },
    async onChangePassword() {
      const id = this.id || this.single?.id
      const propsData = { action: this.updateSecurity, id }
      const modal = await createUsersModalPassword(this, propsData)
      modal.open()
    },
  },
}
</script>
